import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { MessageSquareQuote, CalendarCheck, PhoneCall, MapPin, BadgeCheck } from 'lucide-react';

// Normalize a Turkish city name to match SVG data-iladi attribute.
const normalize = (s) => (s || '').trim().toLocaleLowerCase('tr-TR');

const BASE_FILL = '#fecdd3';
const HOVER_FILL = '#fb7185';
const PULSE_FILL = '#e11d48';
const PULSE_DURATION_MS = 2600;
const TOOLTIP_DURATION_MS = 3200;

const TYPE_META = {
  quote:      { Icon: MessageSquareQuote, verb: 'teklif istedi',           accent: 'bg-rose-50 text-rose-700 ring-rose-200' },
  visit:      { Icon: CalendarCheck,      verb: 'ziyaret randevusu aldı',  accent: 'bg-violet-50 text-violet-700 ring-violet-200' },
  callback:   { Icon: PhoneCall,          verb: 'aranmak istedi',          accent: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  phone_call: { Icon: PhoneCall,          verb: 'davet evini aradı',       accent: 'bg-amber-50 text-amber-700 ring-amber-200' }
};

const TurkeyMap = forwardRef(({ onCityClick }, ref) => {
  const wrapperRef = useRef(null);       // outer positioned wrapper
  const containerRef = useRef(null);     // inner SVG mount point
  const [svgLoaded, setSvgLoaded] = useState(false);
  const [tooltip, setTooltip] = useState(null); // {x, y, event, id}
  const cityIndexRef = useRef({});
  const pulseTimersRef = useRef({});
  const tooltipTimerRef = useRef(null);
  const tooltipIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/turkey-map.svg', { cache: 'force-cache' });
        if (!res.ok) return;
        const svgText = await res.text();
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svgText;

        const svg = containerRef.current.querySelector('svg');
        if (svg) {
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svg.style.width = '100%';
          svg.style.height = 'auto';
          svg.style.display = 'block';
        }

        const provinces = containerRef.current.querySelectorAll('g[data-iladi]');
        // IDs to hide entirely (Cyprus is outside Turkey)
        const HIDDEN_IDS = new Set(['guney-kibris', 'kuzey-kibris', 'kibris']);
        provinces.forEach((g) => {
          // Remove Cyprus elements from view
          if (HIDDEN_IDS.has(g.getAttribute('id'))) {
            g.style.display = 'none';
            return;
          }
          const key = normalize(g.getAttribute('data-iladi'));
          if (!key) return;          cityIndexRef.current[key] = g;
          g.style.transition = 'fill 260ms ease-out';
          g.style.cursor = 'pointer';
          g.querySelectorAll('path').forEach((p) => {
            p.setAttribute('fill', BASE_FILL);
            p.setAttribute('stroke', '#ffffff');
            p.setAttribute('stroke-width', '0.6');
            p.style.transition = 'fill 260ms ease-out';
          });

          g.addEventListener('mouseenter', () => {
            g.querySelectorAll('path').forEach((p) => {
              if (p.dataset.pulsing !== '1') p.setAttribute('fill', HOVER_FILL);
            });
          });
          g.addEventListener('mouseleave', () => {
            g.querySelectorAll('path').forEach((p) => {
              if (p.dataset.pulsing !== '1') p.setAttribute('fill', BASE_FILL);
            });
          });

          g.addEventListener('click', () => {
            if (onCityClick) onCityClick(g.getAttribute('data-iladi'));
          });
        });

        // After hiding Cyprus, tighten viewBox to the mainland's bounding box
        // so the empty bottom area is removed. Uses SVG native getBBox().
        try {
          const root = containerRef.current.querySelector('#turkiye') || svg;
          if (svg && root && typeof root.getBBox === 'function') {
            const bb = root.getBBox();
            if (bb && bb.width > 0 && bb.height > 0) {
              const pad = 4;
              svg.setAttribute(
                'viewBox',
                `${Math.max(0, bb.x - pad)} ${Math.max(0, bb.y - pad)} ${bb.width + pad * 2} ${bb.height + pad * 2}`
              );
            }
          }
        } catch (e) { /* ignore */ }

        setSvgLoaded(true);
      } catch (e) { /* silent */ }
    })();
    return () => {
      cancelled = true;
      Object.values(pulseTimersRef.current).forEach(clearTimeout);
      pulseTimersRef.current = {};
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // Compute tooltip position relative to the wrapper based on province bbox.
  // On mobile (< 640px) pin the tooltip to the BOTTOM of the map so it
  // never overflows or hides the header/province.
  const computeTooltipPos = (g) => {
    try {
      const wRect = wrapperRef.current.getBoundingClientRect();
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      if (isMobile) {
        return { x: wRect.width / 2, y: 0, mode: 'mobile-bottom' };
      }
      const gRect = g.getBoundingClientRect();
      const rawX = gRect.left + gRect.width / 2 - wRect.left;
      const rawY = gRect.top - wRect.top;
      const HALF_W = 140;
      const clampedX = Math.max(HALF_W + 8, Math.min(wRect.width - HALF_W - 8, rawX));
      const mode = rawY < 80 ? 'flip-below' : 'above';
      const y = mode === 'flip-below' ? (gRect.bottom - wRect.top) : rawY;
      return { x: clampedX, y, mode };
    } catch (e) {
      return { x: 0, y: 0, mode: 'above' };
    }
  };

  // Public API: pulse a city and optionally show a tooltip with event details
  const pulseCity = useCallback((cityName, eventData = null) => {
    const key = normalize(cityName);
    const g = cityIndexRef.current[key];
    if (!g) return false;

    if (pulseTimersRef.current[key]) clearTimeout(pulseTimersRef.current[key]);

    const paths = g.querySelectorAll('path');
    paths.forEach((p) => {
      p.dataset.pulsing = '1';
      p.setAttribute('fill', PULSE_FILL);
    });
    try { g.parentNode.appendChild(g); } catch (e) { /* ignore */ }

    pulseTimersRef.current[key] = setTimeout(() => {
      paths.forEach((p) => {
        p.dataset.pulsing = '0';
        p.setAttribute('fill', BASE_FILL);
      });
      delete pulseTimersRef.current[key];
    }, PULSE_DURATION_MS);

    if (eventData) {
      const pos = computeTooltipPos(g);
      const id = ++tooltipIdRef.current;
      setTooltip({ ...pos, event: eventData, id });
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = setTimeout(() => {
        setTooltip((cur) => (cur && cur.id === id ? null : cur));
      }, TOOLTIP_DURATION_MS);
    }

    return true;
  }, []);

  useImperativeHandle(ref, () => ({ pulseCity, isReady: () => svgLoaded }), [pulseCity, svgLoaded]);

  const tooltipEvent = tooltip?.event;
  const meta = tooltipEvent ? (TYPE_META[tooltipEvent.type] || TYPE_META.quote) : null;

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid="turkey-map-wrapper">
      {!svgLoaded && (
        <div className="w-full aspect-[1007/527] rounded-xl bg-gradient-to-br from-rose-50 to-pink-50 animate-pulse" />
      )}
      <div
        ref={containerRef}
        className="w-full"
        aria-label="Türkiye canlı aktivite haritası"
        data-testid="turkey-map-svg"
      />

      {/* Floating tooltip that follows the pulsed province (mobile: bottom banner) */}
      {tooltip && meta && (
        <div
          key={tooltip.id}
          className="pointer-events-none absolute z-20"
          style={
            tooltip.mode === 'mobile-bottom'
              ? {
                  left: tooltip.x,
                  bottom: 6,
                  transform: 'translateX(-50%)',
                  animation: 'mapTooltipIn 260ms ease-out both'
                }
              : {
                  left: tooltip.x,
                  top: tooltip.y,
                  transform:
                    tooltip.mode === 'flip-below'
                      ? 'translate(-50%, 10px)'
                      : 'translate(-50%, calc(-100% - 8px))',
                  animation: 'mapTooltipIn 260ms ease-out both'
                }
          }
          data-testid="map-tooltip"
        >
          <div className="relative bg-white rounded-xl ring-1 ring-rose-200 shadow-[0_10px_30px_-10px_rgba(244,63,94,0.45)] px-3 py-2 min-w-[200px] max-w-[280px]">
            <div className="flex items-start gap-2.5">
              <div className={`shrink-0 mt-0.5 h-7 w-7 rounded-full ring-1 flex items-center justify-center ${meta.accent}`}>
                <meta.Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="font-semibold text-gray-900 text-[13px]">{tooltipEvent.maskedName}</span>
                  <span className="text-gray-500 text-[12px]">{meta.verb}</span>
                </div>
                {tooltipEvent.venueName && (
                  <div className="mt-0.5 flex items-center gap-1 text-[12px] font-medium text-gray-700 truncate">
                    <span className="truncate">{tooltipEvent.venueName}</span>
                    {tooltipEvent.venueClaimed && (
                      <BadgeCheck
                        className="h-3.5 w-3.5 shrink-0 text-sky-500 fill-sky-100"
                        strokeWidth={2.2}
                        aria-label="Onaylı davet evi"
                      />
                    )}
                  </div>
                )}
                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
                  <MapPin className="h-2.5 w-2.5 shrink-0" strokeWidth={2.4} />
                  <span className="truncate">
                    {[tooltipEvent.district, tooltipEvent.city].filter(Boolean).join(', ')}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="whitespace-nowrap">{tooltipEvent.when}</span>
                </div>
              </div>
            </div>
            {/* Pointer arrow — only for desktop-floating tooltips */}
            {tooltip.mode !== 'mobile-bottom' && (
              <span
                className="absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-white ring-1 ring-rose-200 rotate-45"
                style={{
                  bottom: tooltip.mode === 'flip-below' ? undefined : '-6px',
                  top: tooltip.mode === 'flip-below' ? '-6px' : undefined,
                  clipPath:
                    tooltip.mode === 'flip-below'
                      ? 'polygon(0 0, 100% 0, 0 100%)'
                      : 'polygon(100% 0, 100% 100%, 0 100%)'
                }}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes mapTooltipIn {
          from { opacity: 0; transform: translate(-50%, calc(-100% - 2px)) scale(0.92); }
          to   { opacity: 1; transform: translate(-50%, calc(-100% - 8px)) scale(1); }
        }
      `}</style>
    </div>
  );
});

TurkeyMap.displayName = 'TurkeyMap';

export default TurkeyMap;
